import React, { useRef, useState, useCallback, useMemo } from 'react';
import { Layout } from 'fronton-react';
import { useStoreon } from 'storeon/react';
import { State } from 'store/types';
import {
  CalculatorPanel,
  OtherSection,
  Section,
  UtpCardSection,
  InspGalleryRemote,
} from 'common/components';
import { COOKIES_STORE_NAME, selectIsInspGalleryABEnabled } from 'modules/cookies';
import { MODEL_JSON_STORE_NAME, selectInspirationGalleryData } from 'modules/model-json';
import { CategoriesSection } from 'common/components/kitchen';
import { KAZAKHSTAN } from 'common/constants/region-codes';
import { useIsomorphicLayoutEffect } from 'common/hooks';
import { KitchenGallery } from 'feature/kitchen-gallery';
import { PromoBlock } from 'remotes/promo-block';
import { SpecialOffer } from 'remotes/special-offer';
import {
  CreditBlock,
  KitchenBanner,
  NewKitchenHeader,
  NEW_KITCHEN_UTP_CARD_LIST,
  NewKitchenVideo,
  NewKitchenSignSection,
  NewKitchenReviews,
} from './components';
import { KITCHEN_CALCULATOR_HREF, KITCHEN_ELEMENT_BLOCK, KITCHEN_SIGN_UP_HREF } from '../kitchen-constants';
import { NEW_KITCHEN_LANDING_NAME } from './new-kitchen-constants';

type NewKitchenProps = {
  regionId: string;
};

export const NewKitchen: React.FC<NewKitchenProps> = ({ regionId }) => {
  const [isStickyPanelVisible, setIsStickyPanelVisible] = useState<boolean>(true);
  const signSectionPanelRef = useRef<HTMLDivElement | null>(null);
  const signUpHref = `${KITCHEN_SIGN_UP_HREF}${regionId}`;
  const isRuRegion = regionId !== KAZAKHSTAN;

  const { cookies } = useStoreon<State>(COOKIES_STORE_NAME);
  const { modelJson } = useStoreon<State>(MODEL_JSON_STORE_NAME);
  const isInspGalleryABEnabled = useMemo(() => selectIsInspGalleryABEnabled(cookies), [cookies]);
  const inspirationGalleryData = useMemo(() => selectInspirationGalleryData(modelJson), [modelJson]);

  const handleScroll = useCallback(() => {
    if (signSectionPanelRef) {
      const heightVh = signSectionPanelRef.current?.offsetTop || 0;
      const scrollHeightWithWindow = window.scrollY + window.innerHeight;

      setIsStickyPanelVisible(scrollHeightWithWindow < heightVh);
    }
  }, [signSectionPanelRef]);

  useIsomorphicLayoutEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return (
    <>
      <NewKitchenHeader
        isConsultationFormVisible={!isStickyPanelVisible}
        isRuRegion={isRuRegion}
        signUpHref={signUpHref}
      />
      <Layout>
        <PromoBlock elementBlock={KITCHEN_ELEMENT_BLOCK} />
        <SpecialOffer elementBlock={KITCHEN_ELEMENT_BLOCK} />
        {isInspGalleryABEnabled ? (
          <Section>
            <InspGalleryRemote
              regionId={regionId}
              inspirationGalleryData={inspirationGalleryData}
              analyticsName="landingProektKuhni / ggr"
            />
          </Section>
        ) : (
          <KitchenGallery elementBlock={KITCHEN_ELEMENT_BLOCK} isRuRegion={isRuRegion} />
        )}
        <KitchenBanner elementBlock={KITCHEN_ELEMENT_BLOCK} />
      </Layout>
      {isRuRegion && (
        <>
          <Section>
            <CalculatorPanel
              calculatoHref={KITCHEN_CALCULATOR_HREF}
              description="Узнайте примерную стоимость кухни в&nbsp;онлайн-калькуляторе"
              elementBlock={KITCHEN_ELEMENT_BLOCK}
              elementName="Рассчитать стоимость кухни"
              title="Рассчитать стоимость кухни"
            />
          </Section>
          <Layout>
            <CreditBlock />
          </Layout>
        </>
      )}
      <NewKitchenReviews isRuRegion={isRuRegion} signUpHref={signUpHref} />
      <Layout>
        <UtpCardSection
          isNewAnalytics
          elementBlock={KITCHEN_ELEMENT_BLOCK}
          elementText="Дополнительные преимущества"
          renderModalContent={({ modal: { content: ModalContent } = {} }) =>
            ModalContent && <ModalContent />
          }
          utpCardList={NEW_KITCHEN_UTP_CARD_LIST}
        />
        <NewKitchenVideo />
      </Layout>
      <NewKitchenSignSection panelRef={signSectionPanelRef} regionId={regionId} />
      <Layout>
        <CategoriesSection elementBlock={KITCHEN_ELEMENT_BLOCK} />
      </Layout>
      <OtherSection
        elementBlock={`${KITCHEN_ELEMENT_BLOCK} / other`}
        landingName={NEW_KITCHEN_LANDING_NAME}
        promotionName={`${KITCHEN_ELEMENT_BLOCK}_bottom`}
      />
    </>
  );
};
